import React, { PropsWithChildren, useEffect } from "react";
import { isAxiosError } from "axios";
import { apiClient, apiClientHooks, useRegisterAuthTokenProvider } from "./InitApiClient";
import { queryClient } from "./InitReactQuery";
import { useIdTokenClaims } from "../components/auth0/useIdTokenClaims";
import { usePostHog } from 'posthog-js/react';
import { Box, CircularProgress, Typography } from "@mui/material";
import MaintenanceModal from "../components/MaintenanceModal";

export const useGetMyProfileWithJwt = () =>
  apiClientHooks.useGetMyProfile({
    queries: {
      jwt: true,
    }
  })

export const refetchProfileJwt = () =>
  queryClient.invalidateQueries({ queryKey: apiClientHooks.getKeyByAlias('getMyProfile') })

const RegisterAuthTokenProvider: React.FC = () => {
  const getMyProfile = useGetMyProfileWithJwt()

  useRegisterAuthTokenProvider(
    'profile',
    async () => {
      const jwt = getMyProfile.data?.jwt
      if (!jwt) throw new Error('Profile JWT not yet set')
      return jwt
    },
    [getMyProfile.data?.jwt],
  )

  return null
}

export const useProfileAssured = () => {
  const getMyProfile = useGetMyProfileWithJwt()
  if (!getMyProfile.data) throw new Error('Profile not yet initialised')
  return getMyProfile.data
}

export const useProfileJwt = () => useGetMyProfileWithJwt().data?.jwt

const CreateProfile: React.FC = () => {
  const {
    givenName,
    familyName,
  } = useIdTokenClaims()

  const { idToken } = useIdTokenClaims()

  const onCreateProfile = async () => {
    // Create a profile, using the given name and family name from the Auth0 token (if it exists)
    // Missing fields are defaulted to empty strings
    try {
      const response = await apiClient.createMyProfile({
        given_name: givenName ?? '',
        family_name: familyName ?? '',
      }, {
        headers: { 'x-auth0-id-token': idToken ?? '' },
      })
      console.log("[CreateProfile] Create Response: ", response)
    } catch (error) {
      // DBS-358: the profile exists after all (e.g. a second create racing the first): load it rather than stop here
      if (!isAxiosError(error) || error.response?.data?.error?.type !== 'errProfileExistsByIdentity') throw error
      console.warn('[CreateProfile] Profile already exists; loading it')
    }

    queryClient.invalidateQueries({ queryKey: apiClientHooks.getKeyByAlias('getMyProfile') })
  }

  // Automatically create a profile
  useEffect(() => {
    if (idToken !== undefined) {
      console.debug('[CreateProfile] Creating a profile...')
      onCreateProfile()
    } else {
      console.debug('[CreateProfile] No ID Token yet...')
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [idToken])

  return null
}

const InitRequireProfile: React.FC<PropsWithChildren> = ({ children }) => {
  const getMyProfile = useGetMyProfileWithJwt()
  const posthog = usePostHog()

  useEffect(() => {
    if (getMyProfile.data) {
      console.debug('Profile', { email: getMyProfile.data.email })
      // Tell PostHog to link the Auth0 user to the Profile ID and add the profile information
      // This should mean you can find the user in Posthog by their Auth0 user or Profile ID, and that we'll see their email and name
      posthog?.alias(getMyProfile.data.id)
      posthog?.identify(getMyProfile.data.id, {
        email: getMyProfile.data.email,
        name: `${getMyProfile.data.given_name} ${getMyProfile.data.family_name}`,
        profile_id: getMyProfile.data.id,
        uses_applicant_app: true,
      })
    }
  }, [getMyProfile.data, posthog])

  // DBS-358: no data and no error yet is still loading, not a missing profile
  if (getMyProfile.isInitialLoading || (!getMyProfile.isError && getMyProfile.data === undefined)) {
    // We don't want to flash the create profile page while we're loading state
    return <Box
      display="flex"
      flexDirection="column"
      justifyContent="center"
      alignItems="center"
      height="50vh"
    >
      <Typography variant="h6" sx={{ mb: 2 }} gutterBottom>
        Loading Profile ...
      </Typography>
      <CircularProgress />
    </Box>
  }

  const isProfileNotFound = (() => {
    // getMyProfile returns a 401 error with data of `{ error: { type: 'errProfileNotFound' } }` when the profile is not found, and needs creating
    if (!getMyProfile.isError) return false
    if (!isAxiosError(getMyProfile.error)) return false
    return getMyProfile.error.response?.data?.error?.type === 'errProfileNotFound'
  })()

  if (isProfileNotFound) {
    return <CreateProfile />
  }

  // DBS-358: any other failure (expired session, network, server error) is not a new user: say so instead of creating one
  if (getMyProfile.isError || getMyProfile.data === undefined) {
    console.error('[InitRequireProfile] Error loading profile:', getMyProfile.error)
    return <MaintenanceModal open={true} error="Unable to load profile." />
  }

  return <>
    <RegisterAuthTokenProvider />
    {children}
  </>
}

export default InitRequireProfile
